import { useAuth, useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { Link, useQuinceRouter } from '@src/modules/navigation'
import { Icon } from '@src/quince-components/src/atoms/icon'
import rightArrow from '@src/quince-components/src/atoms/productForm/icons/rightArrow.inline.svg'
import { PAGE_PATH } from '@src/quince-site-common/src/constants/pageData'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import orderAggregatorService from '@src/quince-site-common/src/services/orderAggregatorService'
import {
  OrderAggregatorErrorCodes,
  OrdersType,
  SearchType,
} from '@src/quince-site-common/src/services/orderAggregatorService/orderAggregatorService.types'
import { emitReactNativeEvent } from '@src/quince-site-common/src/utils/crossPlatformEvents'
import { CROSS_PLATFORM_EVENT_TYPES } from '@src/quince-site-common/src/utils/crossPlatformEvents/constants'
import { isHttpClientError } from '@src/quince-site-common/src/utils/HttpClient'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { logoutUserFn } from '@src/quince-site-integrated/src/utils'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { FORM_CONFIG, getFmoTrackingId, MESSAGE_CONFIG } from '../utils'

import ConfirmationModal from './confirmation-modal'
import Header from './header'
import OrderList from './OrderList'
import OtpModal from './OtpModal'
import SearchBox from './searchBox'
import styles from './searchOptions.module.scss'
import { SearchOptionsPayload } from './types'

interface Props {
  searchOptions: SearchType[]
}
export default function SearchOptions(props: Props) {
  const { searchOptions } = props
  const { t } = useTranslation('common')
  const fmoOrderSearchPeriodValue = useStatsigDynamicConfig<number>('fmoOrderSearchPeriodValue')
  const fmoOrderSearchPeriodUnit = useStatsigDynamicConfig<string>('fmoOrderSearchPeriodUnit')
  const [step, setStep] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [searchType, setSearchType] = useState<SearchType>(SearchType.ORDER_NUMBER_ZIP)
  const [noMatchesFound, setNoMatchesFound] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useState<any>(null)
  const [initialSearchData, setInitialSearchData] = useState<OrdersType | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const getActiveOptions = useCallback(() => {
    return searchOptions.filter(option => FORM_CONFIG.optionsList.includes(option))
  }, [searchOptions])
  const [showOtpModal, setShowOtpModal] = useState<boolean>(false)
  const [verificationResource, setVerificationResource] = useState<string>('')

  const [otpRequestId, setOtpRequestId] = useState<string | null>(null)
  const [searchError, setSearchError] = useState<boolean>(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)

  const [firstOption, ...otherOptions] = getActiveOptions()
  const topFieldConfig = FORM_CONFIG[firstOption as SearchType]

  const router = useQuinceRouter()
  const { isLoggedIn } = useAuth()
  const { user } = useCurrentUser()

  const { email = '', customerMetadata } = user ?? {}

  // Reset searchType to first option when step goes back to 0
  useEffect(() => {
    if (step === 0 && firstOption) {
      setSearchType(firstOption)
    }
  }, [step, firstOption])

  const API_PAGE_SIZE = 50

  const getPayloadForSearch = (searchType: SearchType, formValues: Record<number, string>) => {
    switch (searchType) {
      case SearchType.ORDER_NUMBER_ZIP:
        if (!formValues[0] || !formValues[1]) {
          setErrorMessage(t('please_enter_order_and_zip_code'))
          return
        }
        return {
          searchType,
          orderNumber: formValues[0] ?? '',
          zipCode: formValues[1] ?? '',
          page: 0,
          size: API_PAGE_SIZE,
        }
      case SearchType.EMAIL:
        if (!formValues[0]) {
          setErrorMessage(t('please_enter_email_address'))
          return
        }
        if (formValues[0] === email) {
          router.push(PAGE_PATH.MY_ORDERS_RETURNS_ROUTE)
          return
        }
        setShowOtpModal(true)
        setLoading(true)
        setVerificationResource(formValues[0] ?? '')
        return null
      case SearchType.CARD_LAST_FOUR_ZIP:
        if (!formValues[0] || !formValues[1]) {
          setErrorMessage(t('please_enter_card_zip_code'))
          return
        }
        return {
          searchType,
          paymentCardSuffix: formValues[0] ?? '',
          zipCode: formValues[1] ?? '',
          page: 0,
          size: API_PAGE_SIZE,
        }
    }
  }

  const handleSearchError = (error: unknown) => {
    const errCode = isHttpClientError(error) ? error?.response?.data?.errorCode : undefined
    let errMessage = isHttpClientError(error) ? error?.response?.data?.message : t('something_went_wrong')

    if (errCode === OrderAggregatorErrorCodes.FIND_ORDER_NOT_AUTHORIZED) {
      errMessage = errMessage || t('not_authorized_to_view_orders')
    }
    setSearchError(true)
    captureException(new Error(errMessage), {
      feature: FEATURES.FIND_MY_ORDER,
      module: MODULES.FIND_MY_ORDER,
      extraInfo: {
        // @i18n-ignore
        error: error,
        capturedFrom: 'handleSearchError from SearchOptions.tsx',
      },
    })
    setNoMatchesFound(true)
    setInitialSearchData(null)
  }

  const executeSearch = async (payload: SearchOptionsPayload) => {
    setNoMatchesFound(false)
    setLoading(true)
    setSearchError(false)
    logEvent(payload)
    try {
      setSearchParams(payload)
      const data = await orderAggregatorService.getSearchResults(payload)
      trackEvent(TELEMETRY_EVENT_NAMES.ORDER_SEARCH_RESULTS, {
        formData: {
          searchField: payload.searchType,
          ...getFmoTrackingId(customerMetadata),
          orderResults: data?.orders?.length ?? 0,
        },
      })
      if (data?.orders?.length === 0) {
        setNoMatchesFound(true)
        setInitialSearchData(null)
      } else {
        setInitialSearchData(data)
        setStep(2)
      }
    } catch (error: any) {
      handleSearchError(error)
    } finally {
      setLoading(false)
    }
  }

  const logEvent = (payload: SearchOptionsPayload) => {
    trackEvent(TELEMETRY_EVENT_NAMES.ORDER_SEARCH_STARTED, {
      formData: { searchField: payload.searchType, ...getFmoTrackingId(customerMetadata) },
    })
  }

  const handleSearch = async (formValues: Record<number, string>) => {
    setErrorMessage('')
    setSearchError(false)
    const payload = getPayloadForSearch(searchType, formValues)
    if (!payload) {
      return
    }
    await executeSearch(payload)
  }

  const handleVerificationSuccess = async (verifiedOtpRequestId: string) => {
    setShowOtpModal(false)
    const payload = {
      searchType: SearchType.EMAIL,
      email: verificationResource,
      authenticationModeId: verifiedOtpRequestId,
      page: 0,
      size: API_PAGE_SIZE,
    }
    await executeSearch(payload)
  }
  const isNativeViewClient = getIsNativeViewClient()

  const handleSignOut = useCallback(async () => {
    try {
      trackEvent(TELEMETRY_EVENT_NAMES.SIGN_IN_STARTED, {
        properties: {
          actionName: TELEMETRY_EVENT_NAMES.SIGN_IN_STARTED,
          actionData: `Sign In Started`,
          ...getFmoTrackingId(customerMetadata),
        },
      })
      if (isNativeViewClient) {
        emitReactNativeEvent(CROSS_PLATFORM_EVENT_TYPES.SWITCH_ACCOUNT_TRIGGERED, {
          // eslint-disable-next-line i18next/no-literal-string
          from: 'find-my-order-search-page',
        })
      } else {
        await logoutUserFn(false, true, false, true)
      }
    } catch (error) {
      captureException(new Error('Error during logout'), {
        feature: FEATURES.FIND_MY_ORDER,
        module: MODULES.FIND_MY_ORDER,
        extraInfo: {
          // @i18n-ignore
          error,
          capturedFrom: 'handleSignOut from SearchOptions.tsx',
        },
      })
    }
  }, [customerMetadata?.customerId, isNativeViewClient])

  const renderErrorMessage = useMemo(() => {
    return (
      <div className={styles.emptyOrderContainer}>
        <p className={styles.emptyOrderTitle}>
          {searchError
            ? t(MESSAGE_CONFIG.error.title)
            : t(`no_matches_found_${fmoOrderSearchPeriodUnit}`, { count: fmoOrderSearchPeriodValue })}
        </p>
        <p>
          {t(searchError ? MESSAGE_CONFIG.error.subtitle : MESSAGE_CONFIG.success.subtitle)}
          {searchError && (
            <>
              <Link href="/contact-us">
                <span className={styles.contactUsSuccessTextLink}>{t(MESSAGE_CONFIG.error.description.label2)}</span>
              </Link>
              <span>{t(MESSAGE_CONFIG.error.description.label3)}</span>
            </>
          )}
        </p>

        {!searchError && (
          <p className={styles.contactUsText}>
            {t(MESSAGE_CONFIG.success.description.label1)}
            <Link href="/contact-us">
              <span className={classNames(styles.contactUsTextLink)}>
                {t(MESSAGE_CONFIG.success.description.label2)}
              </span>
            </Link>
            {t(MESSAGE_CONFIG.success.description.label3)}
          </p>
        )}
      </div>
    )
  }, [searchError, t])

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className={styles.searchOptionsContainer}>
            <SearchBox
              key={`${firstOption}-${step}`}
              fields={topFieldConfig.fields}
              loading={loading}
              setLoading={setLoading}
              setStep={setStep}
              step={step}
              handleSubmit={handleSearch}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              resetSearchError={() => {
                setSearchError(false)
                setNoMatchesFound(false)
              }}
            />

            <div className={styles.header}>
              {isLoggedIn ? t('try_another_way_to_find') : t('try_another_way_to_find_account')}
            </div>
            {otherOptions.map((option, index) => {
              const formOption = FORM_CONFIG[option]
              return (
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSearchType(option)
                      setNoMatchesFound(false)
                      setSearchError(false)
                      setStep(step + 1)
                    }
                  }}
                  onClick={() => {
                    setSearchType(option)
                    setErrorMessage('')
                    setNoMatchesFound(false)
                    setSearchError(false)
                    setStep(step + 1)
                  }}
                  key={index}
                  className={styles.optionContainer}
                >
                  {t(formOption.searchDisplayText)}
                  <div>
                    <Icon iconSvg={rightArrow} />
                  </div>
                </div>
              )
            })}
          </div>
        )

      case 1: {
        const fields = FORM_CONFIG[searchType]
        return (
          <div className={styles.searchOptionsContainer}>
            <SearchBox
              key={`${searchType}-${step}`}
              handleSubmit={handleSearch}
              fields={fields.fields}
              loading={loading}
              setLoading={setLoading}
              setStep={setStep}
              step={step}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              resetSearchError={() => {
                setSearchError(false)
                setNoMatchesFound(false)
              }}
            />
            {searchType === SearchType.CARD_LAST_FOUR_ZIP && (
              <div className={styles.disclaimer}>
                <span>{t('payment_information_disclaimer')} </span>
                <Link href="/contact-us">
                  <span className={classNames(styles.contactUsTextLink, styles.contactUs)}>{t('contact_us_v4')}</span>
                </Link>
                <span>{t('if_you_have_any_questions')}</span>
              </div>
            )}
          </div>
        )
      }
      case 2: {
        return (
          <OrderList
            setShowConfirmationModal={setShowConfirmationModal}
            setStep={setStep}
            searchParams={searchParams}
            initialData={initialSearchData}
            onSignOut={() => void handleSignOut()}
          />
        )
      }
      default: {
        return (
          <div className={styles.searchOptionsContainer}>
            <SearchBox
              key={`${firstOption}-${step}`}
              fields={topFieldConfig.fields}
              loading={loading}
              setLoading={setLoading}
              setStep={setStep}
              step={step}
              handleSubmit={handleSearch}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              resetSearchError={() => {
                setSearchError(false)
                setNoMatchesFound(false)
              }}
            />

            <div className={styles.header}>
              {isLoggedIn ? t('try_another_way_to_find') : t('try_another_way_to_find_account')}
            </div>
            {otherOptions.map((option, index) => {
              const formOption = FORM_CONFIG[option]
              return (
                <div
                  onClick={() => {
                    setErrorMessage('')
                    setSearchType(option)
                    setNoMatchesFound(false)
                    setSearchError(false)
                    setStep(step + 1)
                  }}
                  key={index}
                  className={styles.optionContainer}
                >
                  {t(formOption.searchDisplayText)}
                  <div>
                    <Icon iconSvg={rightArrow} />
                  </div>
                </div>
              )
            })}
          </div>
        )
      }
    }
  }
  return (
    <>
      <div
        className={classNames(
          styles.searchOptionsWrapper,
          step !== 2 && styles.findMyOrderContainer,
          step === 2 && styles.searchResultsContainer,
        )}
      >
        {step !== 2 && (
          <div className={styles.headerWrapper}>
            <Header
              setSearchError={setSearchError}
              setNoMatchesFound={setNoMatchesFound}
              step={step}
              setStep={setStep}
              searchType={searchType}
            />
          </div>
        )}
        {noMatchesFound && renderErrorMessage}

        <div>{renderStep()}</div>
        {showOtpModal && (
          <OtpModal
            open={showOtpModal}
            verificationResource={verificationResource}
            onClose={() => {
              setShowOtpModal(false)
              setOtpRequestId(null)
              setVerificationResource('')
            }}
            onModalOpen={() => {
              setLoading(false)
            }}
            setShowOtpModal={setShowOtpModal}
            otpRequestId={otpRequestId}
            setOtpRequestId={setOtpRequestId}
            onSuccess={otpRequestId => void handleVerificationSuccess(otpRequestId)}
            onError={(showNoResultError: boolean) => {
              setNoMatchesFound(true)
              setSearchError(showNoResultError)
            }}
            resetErrorState={() => {
              setNoMatchesFound(false)
              setSearchError(false)
            }}
          />
        )}
      </div>

      {isLoggedIn && (
        <ConfirmationModal
          open={showConfirmationModal}
          onClose={() => {
            setShowConfirmationModal(false)
          }}
          onSignOut={() => void handleSignOut()}
        />
      )}
    </>
  )
}
